import React, { useState } from 'react';
import { StarIconFilled } from '@/components/icons/StarIcon-Filled';
import { StarIconOutlined } from '@/components/icons/StarIcon-Outlined';
import { saveBookRating } from '@/utils/audioProgressUtils';
import { bookAnalytics } from '@/services/bookAnalytics';
import { inputMethodFromEvent } from '@/services/analyticsContext';
import type { Book } from '@/utils/bookRouting';

interface BookRatingProps {
  book: Book;
  progressSeconds: number;
  initialRating: number;
  isDarkMode: boolean;
}

const BookRating: React.FC<BookRatingProps> = ({
  book,
  progressSeconds,
  initialRating,
  isDarkMode,
}) => {
  const [rating, setRating] = useState<number>(initialRating);
  const [hoverRating, setHoverRating] = useState<number | null>(null);

  const effectiveRating = hoverRating !== null ? hoverRating : rating;

  const handleRatingHover = (val: number) => {
    if (hoverRating === val) return;
    setHoverRating(val);
  };

  const handleStarHover = (e: React.MouseEvent<HTMLButtonElement>, star: number) => {
    if (star === 1) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const val = x < rect.width / 2 ? 0 : 1;
      handleRatingHover(val);
    } else {
      handleRatingHover(star);
    }
  };

  const handleStarClick = (e: React.MouseEvent<HTMLButtonElement>, star: number) => {
    let newVal = star;
    if (star === 1) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      newVal = x < rect.width / 2 ? 0 : 1;
    }
    
    bookAnalytics.track('Book Rated', book, {
      rating: newVal,
      previous_rating: rating,
      surface: 'my_books',
      trigger: 'button',
      input_method: inputMethodFromEvent(e),
    }, progressSeconds);
    setRating(newVal);
    saveBookRating(book.id, newVal);
  };

  const handleRatingLeave = () => {
    setHoverRating(null);
  };

  return (
    <div className="flex items-center gap-0.5 md:gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          onMouseMove={star === 1 ? (e) => handleStarHover(e, star) : undefined}
          onMouseEnter={star !== 1 ? () => handleRatingHover(star) : undefined}
          onMouseLeave={handleRatingLeave}
          onClick={(e) => handleStarClick(e, star)}
          className="hover:scale-110 transition-transform p-1 -m-1"
        >
          {star <= effectiveRating ? (
            <StarIconFilled size="sm" color="var(--color-blue-700)" />
          ) : (
            <StarIconOutlined
              size="sm"
              color={isDarkMode ? "#4b5563" : "#9ca3af"}
            />
          )}
        </button>
      ))}
    </div>
  );
};

export default BookRating;
